import React, { useState } from 'react';
import { X, Target } from 'lucide-react';
import { apiClient } from '../services/api';
import { DailyReviewGoal } from '../types/api';

interface CreateGoalModalProps {
  onClose: () => void;
  onCreated: (goal: DailyReviewGoal) => void;
}

const DURATION_OPTIONS = [
  { label: '1 week', value: 7 },
  { label: '2 weeks', value: 14 },
  { label: '3 weeks', value: 21 },
  { label: '1 month', value: 30 },
];

export const CreateGoalModal: React.FC<CreateGoalModalProps> = ({ onClose, onCreated }) => {
  const [dailyTarget, setDailyTarget] = useState('');
  const [durationDays, setDurationDays] = useState(7);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const target = parseInt(dailyTarget, 10);
    if (!target || target < 1) {
      setError('Daily target must be at least 1');
      return;
    }
    setError(null);
    setIsLoading(true);
    try {
      const goal = await apiClient.createDailyReviewGoal({
        daily_target: target,
        start_date: new Date().toISOString().split('T')[0],
        duration_days: durationDays,
      });
      onCreated(goal);
    } catch {
      setError('Failed to create goal. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 flex items-center justify-center rounded-full bg-primary-100">
              <Target className="h-5 w-5 text-primary-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">New Daily Goal</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm">{error}</div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cards to review per day
            </label>
            <input
              type="number"
              min={1}
              value={dailyTarget}
              onChange={e => setDailyTarget(e.target.value)}
              required
              placeholder="e.g. 10"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Duration
            </label>
            <div className="grid grid-cols-2 gap-2">
              {DURATION_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setDurationDays(opt.value)}
                  className={`py-2 px-3 rounded-md text-sm font-medium border transition-colors ${
                    durationDays === opt.value
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 text-sm font-medium"
            >
              {isLoading ? 'Creating...' : 'Create goal'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
